/**
 * @fileoverview TipTap Formatting Marks Extension (MOR-1035)
 *
 * Word-style "show editing marks": a toolbar toggle reveals paragraph
 * marks (¶), space dots (·), non-breaking-space rings (°) and line-break
 * arrows (↵) so template authors can see exactly what whitespace sits
 * between a field chip and the word that follows it.
 *
 * Implementation:
 *  - Spaces / nbsp are ProseMirror inline decorations (a class on the
 *    character), so they track edits for free and cost nothing while
 *    hidden (the decoration set is empty when the toggle is off).
 *  - Hard breaks get a widget decoration (a ↵ glyph before the <br>),
 *    because `br::after` doesn't render cross-browser.
 *  - Paragraph marks are pure CSS (`p::after`) scoped under the
 *    `show-formatting-marks` class the toggle sets on the editor DOM.
 *
 * All marks are visual-only: decorations and CSS never touch the
 * document, so saved HTML is byte-identical whichever way the toggle
 * sits.
 */

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

/** Per-editor state for the formatting marks toggle. */
export interface FormattingMarksStorage {
  /** Whether the marks are currently shown. */
  visible: boolean;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    formattingMarks: {
      /** Show or hide the whitespace/paragraph marks. */
      setFormattingMarks: (visible: boolean) => ReturnType;
    };
  }
  interface Storage {
    formattingMarks: FormattingMarksStorage;
  }
}

const pluginKey = new PluginKey<DecorationSet>("formattingMarks");

/** CSS class the toggle sets on the ProseMirror root for the ¶ rules. */
export const FORMATTING_MARKS_CLASS = "show-formatting-marks";

function buildDecorations(doc: ProseMirrorNode): DecorationSet {
  const decorations: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (node.isText && node.text) {
      const text = node.text;
      for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (ch === " ") {
          decorations.push(
            Decoration.inline(pos + i, pos + i + 1, {
              class: "fmt-mark-space",
            }),
          );
        } else if (ch === "\u00A0") {
          decorations.push(
            Decoration.inline(pos + i, pos + i + 1, {
              class: "fmt-mark-nbsp",
            }),
          );
        }
      }
    } else if (node.type.name === "hardBreak") {
      decorations.push(
        Decoration.widget(
          pos,
          () => {
            const el = document.createElement("span");
            el.className = "fmt-mark-break";
            el.setAttribute("aria-hidden", "true");
            el.textContent = "↵";
            return el;
          },
          { side: -1 },
        ),
      );
    }
    return true;
  });
  return DecorationSet.create(doc, decorations);
}

export const FormattingMarks = Extension.create({
  name: "formattingMarks",

  addStorage(): FormattingMarksStorage {
    return { visible: false };
  },

  addCommands() {
    return {
      setFormattingMarks:
        (visible: boolean) =>
        ({ editor, tr, dispatch }) => {
          this.storage.visible = visible;
          editor.view.dom.classList.toggle(FORMATTING_MARKS_CLASS, visible);
          if (dispatch) {
            // Meta ping so the plugin rebuilds (or clears) its decorations.
            tr.setMeta(pluginKey, visible);
            dispatch(tr);
          }
          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const storage = this.storage;
    return [
      new Plugin<DecorationSet>({
        key: pluginKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, old, _oldState, newState) {
            const toggled = tr.getMeta(pluginKey) as boolean | undefined;
            if (toggled !== undefined) {
              return toggled
                ? buildDecorations(newState.doc)
                : DecorationSet.empty;
            }
            if (!storage.visible) return DecorationSet.empty;
            if (tr.docChanged) return buildDecorations(newState.doc);
            return old;
          },
        },
        props: {
          decorations(state) {
            return pluginKey.getState(state);
          },
        },
      }),
    ];
  },
});
